import { useId } from 'react';
import { Button } from '@bb/ui';
import type { ExtraDefinitionView, ExtrasCheck, ExtrasSchemaView } from '../../../api/client.ts';

const split = (raw: string | undefined) =>
  (raw ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

const applies = (def: ExtraDefinitionView, value: Record<string, string>) => !def.appliesWhen || split(value[def.appliesWhen.key]).some((v) => def.appliesWhen!.in.includes(v));

interface Props {
  schema: ExtrasSchemaView;
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  /** The latest check of `value` (server `validate`), for messages under the fields. */
  check?: ExtrasCheck | null;
  readOnly?: boolean;
}

function Messages({ path, check }: { path: string; check?: ExtrasCheck | null }) {
  const errors = check?.errors.filter((e) => e.path === path) ?? [];
  const warnings = check?.warnings.filter((w) => w.path === path) ?? [];
  return (
    <>
      {errors.map((e) => (
        <p key={e.code} className="field-error" role="alert">
          {e.message}
        </p>
      ))}
      {warnings.map((w) => (
        <p key={w.code} className="hint tag--warning">
          {w.message}
        </p>
      ))}
    </>
  );
}

/**
 * Extras of a message as a form built from the environment's extras schema (BB-58): a select for an enum, checkboxes
 * for an enum list, true/false for a boolean, text for the rest. Fields that apply only when another extra has a value
 * appear when it does. Keys outside the schema stay editable as raw pairs unless the schema is strict.
 */
export function TypedExtrasForm({ schema, value, onChange, check, readOnly = false }: Props) {
  const ids = useId();
  const set = (key: string, raw: string) => {
    const next = { ...value };
    if (raw === '') delete next[key];
    else next[key] = raw;
    onChange(next);
  };
  const known = new Set(schema.items.map((d) => d.key));
  const fields = schema.items.filter((d) => (!d.deprecated || d.key in value) && (applies(d, value) || d.key in value));
  const others = Object.entries(value).filter(([key]) => !known.has(key));
  const rename = (from: string, to: string) => onChange(Object.fromEntries(Object.entries(value).map(([k, v]) => [k === from ? to : k, v])));

  return (
    <div className="typed-extras">
      {fields.map((def) => {
        const id = `${ids}-${def.key}`;
        const raw = value[def.key] ?? '';
        const label = (
          <label htmlFor={id}>
            {def.key}
            {def.required && applies(def, value) ? ' *' : ''}
            {def.deprecated ? ' (deprecated)' : ''}
          </label>
        );
        let control;
        if (def.type === 'enum' || def.type === 'boolean') {
          const options = def.type === 'enum' ? def.enumValues : ['true', 'false'];
          control = (
            <select id={id} value={raw} disabled={readOnly} onChange={(e) => set(def.key, e.target.value)}>
              <option value="">—</option>
              {options.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          );
        } else if (def.type === 'enum_list') {
          const chosen = split(raw);
          return (
            <fieldset key={def.key} className="typed-extras__field">
              <legend>
                {def.key}
                {def.required && applies(def, value) ? ' *' : ''}
              </legend>
              {def.enumValues.map((o) => (
                <label key={o} className="inline">
                  <input
                    type="checkbox"
                    checked={chosen.includes(o)}
                    disabled={readOnly}
                    onChange={(e) => set(def.key, (e.target.checked ? [...chosen, o] : chosen.filter((c) => c !== o)).filter((c, i, all) => all.indexOf(c) === i).join(','))}
                  />{' '}
                  {o}
                </label>
              ))}
              {def.description && <p className="hint">{def.description}</p>}
              <Messages path={`extras.${def.key}`} check={check} />
            </fieldset>
          );
        } else if (def.type === 'json') {
          control = <textarea id={id} value={raw} disabled={readOnly} spellCheck={false} onChange={(e) => set(def.key, e.target.value)} />;
        } else {
          control = <input id={id} value={raw} disabled={readOnly} spellCheck={false} inputMode={def.type === 'number' ? 'decimal' : undefined} onChange={(e) => set(def.key, e.target.value)} />;
        }
        return (
          <div key={def.key} className="typed-extras__field">
            {label} {control}
            {def.description && <p className="hint">{def.description}</p>}
            <Messages path={`extras.${def.key}`} check={check} />
          </div>
        );
      })}
      <fieldset className="typed-extras__raw">
        <legend>Other key-value pairs</legend>
        {schema.strict && <p className="hint">This schema is strict: keys outside it are refused.</p>}
        {others.map(([key, raw], i) => (
          <div key={i} className="typed-extras__pair">
            <input aria-label={`Key ${i + 1}`} value={key} disabled={readOnly} onChange={(e) => rename(key, e.target.value)} />
            <input aria-label={`Value ${i + 1}`} value={raw} disabled={readOnly} onChange={(e) => onChange({ ...value, [key]: e.target.value })} />
            {!readOnly && (
              <Button variant="ghost" aria-label={`Remove ${key}`} onClick={() => set(key, '')}>
                Remove
              </Button>
            )}
            <Messages path={`extras.${key}`} check={check} />
          </div>
        ))}
        {!readOnly && !schema.strict && (
          <Button variant="ghost" onClick={() => onChange({ ...value, [`key-${others.length + 1}`]: '' })}>
            Add key-value pair
          </Button>
        )}
      </fieldset>
    </div>
  );
}
